(function (root, factory) {
  const scanner = factory();
  if (typeof module === 'object' && module.exports) module.exports = scanner;
  if (root) root.SownIngredientScanner = scanner;
})(typeof globalThis === 'object' ? globalThis : this, function () {
  'use strict';

  const names = Object.freeze([
    'Isopropyl Cloprostenate',
    'Dechloro Dihydroxy Difluoro Ethylcloprostenolamide',
    'Methylamido-Dihydro-Noralfaprostal',
    'Bimatoprost',
    'Latanoprost',
    'Travoprost',
    'Tafluprost',
    'Cloprostenol',
    'Cloprostenol Isopropyl Ester',
    'Prostaglandin F2α',
    'Prostaglandin F2α analogues',
    'Noralfaprostal derivatives',
    'Ethyl Tafluprostamide',
    'Tafluprostamide',
    'Bimatoprost analogues'
  ]);

  function normalize(value) {
    let output = '';
    const starts = [];
    const ends = [];
    let sourceIndex = 0;
    let lastWasSpace = true;

    for (const sourceChar of value) {
      const sourceStart = sourceIndex;
      sourceIndex += sourceChar.length;
      const sourceEnd = sourceIndex;
      const decomposed = sourceChar.normalize('NFKD').toLowerCase();

      for (const char of decomposed) {
        if (/\p{M}/u.test(char)) continue;
        const replacement = char === 'α' ? 'alpha' : /[a-z0-9]/.test(char) ? char : /[,;]/.test(char) ? ';' : /[\r\n]/.test(char) ? '\n' : ' ';
        for (const normalizedChar of replacement) {
          if (normalizedChar === ';' || normalizedChar === '\n') {
            output += normalizedChar;
            starts.push(sourceStart);
            ends.push(sourceEnd);
            lastWasSpace = true;
            continue;
          }
          if (normalizedChar === ' ') {
            if (lastWasSpace) continue;
            output += ' ';
            starts.push(sourceStart);
            ends.push(sourceEnd);
            lastWasSpace = true;
          } else {
            output += normalizedChar;
            starts.push(sourceStart);
            ends.push(sourceEnd);
            lastWasSpace = false;
          }
        }
      }
    }

    if (output.endsWith(' ')) {
      output = output.slice(0, -1);
      starts.pop();
      ends.pop();
    }
    return { text: output, starts, ends };
  }

  function canonicalTokens(name) {
    return normalize(name).text.split(' ');
  }

  const entries = names.map((name) => ({ name, tokens: canonicalTokens(name) }));

  function levenshteinAtMostTwo(left, right) {
    if (Math.abs(left.length - right.length) > 2) return 3;
    let previous = Array.from({ length: right.length + 1 }, (_, i) => i);
    for (let i = 1; i <= left.length; i += 1) {
      const current = [i];
      let rowMin = i;
      for (let j = 1; j <= right.length; j += 1) {
        const value = Math.min(
          previous[j] + 1,
          current[j - 1] + 1,
          previous[j - 1] + (left[i - 1] === right[j - 1] ? 0 : 1)
        );
        current[j] = value;
        rowMin = Math.min(rowMin, value);
      }
      if (rowMin > 2) return 3;
      previous = current;
    }
    return previous[right.length];
  }

  function isNegated(normalizedText, start, end) {
    const before = normalizedText.slice(Math.max(0, start - 80), start);
    const after = normalizedText.slice(end, Math.min(normalizedText.length, end + 18));
    return /(?:\bno|\bnot\s+(?:contain|containing|with)|\bwithout|\bfree\s+of)\s*$/.test(before) ||
      /\bdoes\s+not\s+contain\s*$/.test(before) ||
      /\bno\s+prostaglandins?\b(?:[\s,:-]{0,24})$/.test(before) ||
      /^(?:\s*-?\s*free\b|\s+free\b)/.test(after);
  }

  function scan(input) {
    const source = String(input == null ? '' : input);
    const normalized = normalize(source);
    const text = normalized.text;
    const candidates = [];

    for (const entry of entries) {
      const expression = new RegExp(entry.tokens.map(regexToken).join('\\s*'), 'g');
      let match;
      while ((match = expression.exec(text)) !== null) {
        candidates.push({
          name: entry.name,
          start: normalized.starts[match.index],
          end: normalized.ends[match.index + match[0].length - 1],
          normalizedStart: match.index,
          normalizedEnd: match.index + match[0].length,
          kind: isNegated(text, match.index, match.index + match[0].length) ? 'possible' : 'exact',
          exact: true
        });
        if (match[0].length === 0) expression.lastIndex += 1;
      }
    }

    addFuzzyCandidates(text, normalized, candidates);

    // Exact hits win over fuzzy suggestions, then longer overlapping names win.
    candidates.sort((left, right) => Number(right.exact) - Number(left.exact) ||
      (right.end - right.start) - (left.end - left.start) || left.start - right.start);
    const selected = [];
    for (const candidate of candidates) {
      if (selected.some((chosen) => candidate.start < chosen.end && candidate.end > chosen.start)) continue;
      selected.push(candidate);
    }
    selected.sort((left, right) => left.start - right.start || left.end - right.end);

    return { matches: selected.map(({ name, start, end, kind }) => ({ name, start, end, kind })) };
  }

  function addFuzzyCandidates(text, normalized, candidates) {
    const tokenExpression = /[a-z0-9]+/g;
    const tokens = [];
    let match;
    while ((match = tokenExpression.exec(text)) !== null) {
      tokens.push({ value: match[0], start: match.index, end: match.index + match[0].length });
    }

    const segments = [];
    const delimiter = /[;,\n\r]/g;
    let tokenIndex = 0;
    let segment = [];
    while ((match = delimiter.exec(text)) !== null) {
      while (tokenIndex < tokens.length && tokens[tokenIndex].end <= match.index) {
        segment.push(tokens[tokenIndex]);
        tokenIndex += 1;
      }
      segments.push(segment);
      segment = [];
    }
    segments.push(segment.concat(tokens.slice(tokenIndex)));

    for (const segment of segments) {
      // A short delimited segment is ingredient-like; long prose runs are not fuzzy-scanned.
      if (!segment.length || segment.length > 6) continue;
      for (const entry of entries) {
        const size = entry.tokens.length;
        for (let first = 0; first + size <= segment.length; first += 1) {
          const window = segment.slice(first, first + size);
          let distance = 0;
          for (let index = 0; index < size; index += 1) {
            const actual = window[index].value;
            const wanted = entry.tokens[index];
            if (actual === wanted) continue;
            if (actual.length < 8 || wanted.length < 8) {
              distance = 3;
              break;
            }
            distance += levenshteinAtMostTwo(actual, wanted);
            if (distance > 2) break;
          }
          if (distance < 1 || distance > 2) continue;

          const normalizedStart = window[0].start;
          const normalizedEnd = window[window.length - 1].end;
          const start = normalized.starts[normalizedStart];
          const end = normalized.ends[normalizedEnd - 1];
          if (candidates.some((candidate) => start < candidate.end && end > candidate.start)) continue;
          candidates.push({ name: entry.name, start, end, normalizedStart, normalizedEnd, kind: 'possible', exact: false });
        }
      }
    }
  }

  function escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function regexToken(value) {
    if (value === 'f2alpha') return 'f2\\s*(?:alpha|a(?![a-z]))';
    if (value === 'analogues') return '(?:analogues|analogs|analogue|analog)';
    if (value === 'derivatives') return '(?:derivatives|derivative)';
    return escapeRegExp(value);
  }

  return Object.freeze({ scan, names });
});
